import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, AlertCircle, CheckCircle2, Loader2, KeyRound, Shield } from "lucide-react";
import { Modal } from "../ui";
import type { UserRole } from "../../types";
import { getBranding } from "../../config/branding";
import { AuthBrandLogo } from "./AuthBrandLogo";

interface ForgotCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: UserRole | null;
  onRequestVerification: (email: string) => Promise<{ success: boolean; error: string | null; emailSent?: boolean }>;
  onVerifyCode: (email: string, verificationCode: string) => Promise<{ code: string | null; name: string | null; error: string | null }>;
}

type Step = "email" | "verification" | "success";

export function ForgotCodeModal({ isOpen, onClose, role, onRequestVerification, onVerifyCode }: ForgotCodeModalProps) {
  const { t } = useTranslation();
  const branding = getBranding();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const [emailWasSent, setEmailWasSent] = useState(true);
  const [success, setSuccess] = useState<{ code: string; name: string } | null>(null);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!email.trim()) {
        setError(t("auth.emailRequired"));
        return;
      }

      const result = await onRequestVerification(email.trim());
      
      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.success) {
        setStep("verification");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!verificationCode.trim()) {
        setError(t("auth.verificationCodeRequired"));
        return;
      }

      const result = await onVerifyCode(email.trim(), verificationCode.trim());
      
      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.code && result.name) {
        setSuccess({ code: result.code, name: result.name });
        setStep("success");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setResendSuccess(false);
    setVerificationCode("");
    setLoading(true);

    try {
      const result = await onRequestVerification(email.trim());
      
      if (result.error) {
        setError(result.error);
      } else if (result.success) {
        setEmailWasSent(result.emailSent ?? false);
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 4000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("email");
    setEmail("");
    setVerificationCode("");
    setError("");
    setResendSuccess(false);
    setSuccess(null);
    onClose();
  };

  if (!role || role === "admin") return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="flex flex-col items-center text-center mb-8">
        <div className="mb-4 flex flex-col items-center gap-2">
          <AuthBrandLogo branding={branding} size="modal" />
          {branding.logoDisplay !== "full" && (
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 tracking-wide">
              {branding.brandName}
            </p>
          )}
        </div>
        <div className="w-16 h-16 bg-purple-100 dark:bg-slate-700 border border-purple-200 dark:border-white/10 rounded-full flex items-center justify-center mb-4 text-purple-600 dark:text-purple-400">
          {step === "verification" ? <Shield size={28} /> : <KeyRound size={28} />}
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
          {step === "email" && t("auth.forgotCodeTitle")}
          {step === "verification" && t("auth.verification")}
          {step === "success" && t("auth.codeRecovered")}
        </h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
          {step === "email" && t("auth.enterEmail")}
          {step === "verification" && t("auth.enterCodeReceived")}
          {step === "success" && t("auth.yourAccessCodeDesc")}
        </p>
      </div>

      {step === "email" && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div className="group">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-2xl outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-purple-500"
                placeholder={t("auth.emailPlaceholder")}
                autoFocus
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 dark:text-red-400 text-xs font-medium py-1 animate-pulse">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-medium rounded-2xl shadow-lg shadow-purple-500/30 hover:opacity-95 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t("auth.sendVerificationCode")
            )}
          </button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-xs text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
            >
              {t("auth.backToLogin")}
            </button>
          </div>
        </form>
      )}

      {step === "verification" && (
        <form onSubmit={handleVerificationSubmit} className="space-y-4">
          {!emailWasSent && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 mb-2">
              <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
                {t("auth.devEmailNote")}
              </p>
            </div>
          )}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4 mb-2">
            <p className="text-xs text-blue-600 dark:text-blue-400 text-center">
              {emailWasSent
                ? <>{t("auth.codeSentTo")} <strong>{email}</strong></>
                : <>{t("auth.codeGeneratedFor")} <strong>{email}</strong></>}
            </p>
            <p className="text-[11px] text-blue-500 dark:text-blue-400/80 text-center mt-2">
              {t("auth.checkSpamFolder")}
            </p>
          </div>

          <div className="group">
            <div className="relative">
              <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-400" />
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-2xl outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-purple-500 text-center text-2xl font-mono tracking-widest"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
            </div>
          </div>

          {resendSuccess && (
            <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400 text-xs font-medium py-1">
              <CheckCircle2 className="w-3 h-3 shrink-0" />
              {t("auth.newCodeSent")}
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 dark:text-red-400 text-xs font-medium py-1 animate-pulse">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || verificationCode.length !== 6}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-medium rounded-2xl shadow-lg shadow-purple-500/30 hover:opacity-95 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t("auth.verifyCode")
            )}
          </button>

          <div className="pt-3 space-y-2">
            <p className="text-xs text-gray-500 dark:text-slate-400 text-center">
              {t("auth.noEmailReceived")}
            </p>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep("email")}
                className="text-xs text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
              >
                ← {t("auth.changeEmail")}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {t("auth.resendCode")}
              </button>
            </div>
          </div>
        </form>
      )}

      {step === "success" && success && (
        <div className="space-y-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-6">
            <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400 mb-4">
              <CheckCircle2 className="w-6 h-6" />
              <span className="font-semibold">{t("auth.verificationSuccess")}</span>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t("auth.namePlaceholder")}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{success.name}</p>
              </div>
              
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t("auth.yourAccessCode")}</p>
                <div className="bg-white dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl p-4">
                  <p className="text-2xl font-mono font-bold text-center tracking-widest text-purple-600 dark:text-purple-400">
                    {success.code}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium rounded-2xl shadow-lg shadow-blue-500/30 hover:opacity-95 transition-opacity"
          >
            {t("auth.connectNow")}
          </button>
        </div>
      )}
    </Modal>
  );
}
